/**
 * Prompt Context
 *
 * React Context for managing ephemeral prompt state.
 * Prompts are UI-only state (like hover states) and don't need undo/redo.
 *
 * ARCHITECTURE COMPLIANCE:
 * - Prompt state is ephemeral (not persisted to diagram state)
 * - Managed via React Context (not Zustand)
 * - Can be persisted to localStorage for dismissed prompts
 * - NO undo/redo support (it's UI state, not diagram state)
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ContextualPrompt } from '../domains/genogram/models/Prompt';

interface PromptContextValue {
  activePrompts: ContextualPrompt[];
  dismissedPrompts: string[];
  snoozedPrompts: Record<string, number>; // promptId -> timestamp

  // Context methods (not Zustand setters)
  addPrompt: (prompt: ContextualPrompt) => void;
  dismissPrompt: (promptId: string) => void;
  snoozePrompt: (promptId: string, duration: number) => void;
  clearPrompts: () => void;
}

const PromptContext = createContext<PromptContextValue | null>(null);

export const PromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activePrompts, setActivePrompts] = useState<ContextualPrompt[]>([]);
  const [dismissedPrompts, setDismissedPrompts] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('dismissed-prompts');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [snoozedPrompts, setSnoozedPrompts] = useState<Record<string, number>>({});

  // Clean up expired snoozed prompts
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setSnoozedPrompts(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (updated[key] < now) {
            delete updated[key];
          }
        });
        return updated;
      });
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const addPrompt = useCallback((prompt: ContextualPrompt) => {
    // Don't show if dismissed or snoozed
    if (dismissedPrompts.includes(prompt.id)) return;
    if (snoozedPrompts[prompt.id] && snoozedPrompts[prompt.id] > Date.now()) return;

    setActivePrompts(prev => {
      // Don't add duplicates
      if (prev.some(p => p.id === prompt.id)) return prev;
      return [...prev, prompt];
    });
  }, [dismissedPrompts, snoozedPrompts]);

  const dismissPrompt = useCallback((promptId: string) => {
    setActivePrompts(prev => prev.filter(p => p.id !== promptId));
    setDismissedPrompts(prev => {
      const updated = [...prev, promptId];
      try {
        localStorage.setItem('dismissed-prompts', JSON.stringify(updated));
      } catch {
        // Ignore localStorage errors
      }
      return updated;
    });
  }, []);

  const snoozePrompt = useCallback((promptId: string, duration: number) => {
    setActivePrompts(prev => prev.filter(p => p.id !== promptId));
    setSnoozedPrompts(prev => ({
      ...prev,
      [promptId]: Date.now() + duration
    }));
  }, []);

  const clearPrompts = useCallback(() => {
    setActivePrompts([]);
  }, []);

  const value: PromptContextValue = {
    activePrompts,
    dismissedPrompts,
    snoozedPrompts,
    addPrompt,
    dismissPrompt,
    snoozePrompt,
    clearPrompts
  };

  return <PromptContext.Provider value={value}>{children}</PromptContext.Provider>;
};

export const usePrompts = () => {
  const context = useContext(PromptContext);
  if (!context) {
    throw new Error('usePrompts must be used within PromptProvider');
  }
  return context;
};
